import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Chip } from '@weareopenr/component-library';
import type { ColourVariant } from '@weareopenr/component-library';
import { useGetTenants, usePermissions } from '../../hooks';
import { DataTable } from '../../components/DataTable/DataTable';
import type { ITenantSummary, TenantStatus } from '../../types';
import styles from './Tenants.module.scss';

export const STATUS_COLOUR: Record<TenantStatus, ColourVariant> = {
  active: 'kale',
  provisioning: 'honey',
  suspended: 'grape',
  offboarding: 'honey',
  offboarded: 'liquorice',
  failed: 'tomato',
};

export const HEALTH_LABEL: Record<ITenantSummary['health'], string> = {
  ok: 'Healthy',
  warning: 'Needs attention',
  error: 'Failing',
  unknown: 'Unknown',
};

export const formatMoney = (value: number | null): string =>
  value === null ? '—' : `£${value.toLocaleString('en-GB')}`;

export const formatDate = (value: string | null): string =>
  value === null ? '—' : new Date(value).toLocaleDateString('en-GB');

export const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

const HEALTH_ORDER: Record<ITenantSummary['health'], number> = {
  error: 0,
  warning: 1,
  unknown: 2,
  ok: 3,
};

const STATUS_FILTERS: { id: 'all' | TenantStatus; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'provisioning', label: 'Provisioning' },
  { id: 'suspended', label: 'Suspended' },
];

export const Tenants = () => {
  const navigate = useNavigate();
  const { data: tenants = [], isLoading, error } = useGetTenants();
  const { canProvision } = usePermissions();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'all' | TenantStatus>('all');

  const rows = useMemo(
    () => tenants.filter((tenant) => status === 'all' || tenant.status === status),
    [tenants, status],
  );

  const columns = useMemo<ColumnDef<ITenantSummary, any>[]>(
    () => [
      {
        id: 'tenant',
        header: 'Tenant',
        accessorFn: (row) => `${row.display_name} ${row.name}`,
        size: 240,
        cell: ({ row }) => (
          <div className={styles.name}>
            <span className={styles.avatar} aria-hidden="true">
              {initialsOf(row.original.display_name)}
            </span>
            <div className={styles.nameText}>
              <span className={styles.displayName}>{row.original.display_name}</span>
              <span className={styles.key}>{row.original.name}</span>
            </div>
          </div>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        size: 130,
        cell: ({ getValue }) => {
          const value = getValue<TenantStatus>();
          return <Chip label={value} backgroundColour={STATUS_COLOUR[value]} />;
        },
      },
      { header: 'Users', accessorKey: 'users', size: 80, meta: { align: 'right' } },
      { header: 'Brands', accessorKey: 'brands', size: 80, meta: { align: 'right' } },
      {
        header: 'Venues',
        accessorKey: 'venues',
        size: 90,
        meta: { align: 'right' },
        cell: ({ getValue }) => (getValue<number>() ?? 0).toLocaleString('en-GB'),
      },
      {
        header: 'Add-ons',
        accessorKey: 'addons',
        size: 200,
        enableSorting: false,
        cell: ({ getValue }) => {
          const addons = getValue<string[]>();
          return addons.length === 0 ? (
            <span className={styles.muted}>none</span>
          ) : (
            <span className={styles.addons}>
              {addons.map((code) => (
                <span key={code} className={styles.addon}>
                  {code.replace('_', ' ')}
                </span>
              ))}
            </span>
          );
        },
      },
      {
        header: 'Base plan',
        accessorKey: 'base_plan',
        size: 110,
        cell: ({ getValue }) =>
          getValue<string | null>() ?? <span className={styles.muted}>not set</span>,
      },
      {
        header: 'MRR',
        accessorKey: 'mrr',
        size: 100,
        meta: { align: 'right' },
        cell: ({ getValue }) => formatMoney(getValue<number | null>()),
      },
      {
        header: 'Last publish',
        accessorKey: 'last_publish_at',
        size: 120,
        cell: ({ getValue }) => formatDate(getValue<string | null>()),
      },
      {
        header: 'Health',
        accessorKey: 'health',
        size: 160,
        sortingFn: (a, b) => HEALTH_ORDER[a.original.health] - HEALTH_ORDER[b.original.health],
        cell: ({ getValue }) => {
          const health = getValue<ITenantSummary['health']>();
          return (
            <span className={styles.health} data-health={health}>
              <span className={styles.dot} />
              {HEALTH_LABEL[health]}
            </span>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Tenants</h1>
          <p className={styles.subtitle}>
            {isLoading ? 'Loading…' : `${tenants.length} tenants in this environment`}
          </p>
        </div>
        <div className={styles.filters}>
          <div className={styles.segments} role="radiogroup" aria-label="Status">
            {STATUS_FILTERS.map((option) => (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={status === option.id}
                className={status === option.id ? styles.segmentOn : styles.segment}
                onClick={() => setStatus(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <input
            className={styles.search}
            type="search"
            placeholder="Search tenants"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search tenants"
          />
          {canProvision ? (
            <button
              type="button"
              className={styles.primary}
              onClick={() => navigate('/tenants/new')}
            >
              + New tenant
            </button>
          ) : null}
        </div>
      </header>
      {error ? (
        <p className={styles.error}>
          Could not load tenants: {(error as { message?: string }).message ?? 'unknown error'}
        </p>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          storageKey="nexus-ui.tenants.table"
          globalFilter={search}
          onRowClick={(row) => navigate(`/tenants/${row.name}`)}
          emptyMessage={isLoading ? 'Loading tenants…' : 'No tenants match.'}
        />
      )}
    </div>
  );
};
